import React, { type FC, type ReactNode, useMemo } from 'react';
import type { TablePaginationConfig, ColumnsType } from 'antd/es/table';
import { Table, Spin, Empty } from 'antd';
import { Link, Space, Text } from '@g2u/ui-kit';
import { format, parse } from 'date-fns';

import { Condition, type JobSearchResult } from 'types';

import { JobActions } from './job-actions.component';
import styles from './job-list.module.scss';

export namespace JobList {
  export type Action =
    | ((record: JobSearchResult) => ReactNode)
    | {
        title: string;
        action: (record: JobSearchResult) => void;
      };

  type PaginationMetadata = {
    total: number;
    current_page: number;
    pageSize: number;
    onChange: TablePaginationConfig['onChange'];
  };

  export type Props = {
    jobs?: JobSearchResult[];
    actions?: Action[];
    paginationMetadata?: PaginationMetadata;
    hideColumns?: ColumnKeys[];
    isLoading?: boolean;
    columnsDefinition?: ColumnsType<JobSearchResult>;
  };

  type ColumnKeys = (typeof defaultColumns)[number]['key'] | 'actions';

  const defaultColumns = [
    {
      key: 'jobId',
      title: 'Job ID',
      dataIndex: 'ID',
      render: (_, record) => <>{record.ID}</>,
      width: 80,
    },
    {
      key: 'dates',
      title: 'Dates',
      dataIndex: ['appointment'],
      render: (_, record) => {
        const isCancelled = record.cancelled === Condition.Yes;
        return (
          <>
            {record.booking && (
              <div>
                Booked: {format(parse(record.booking, 'yyyy-MM-dd HH:mm:ss', new Date()), 'h:mmaaa dd-MM-yyyy')}
              </div>
            )}
            {record.requested && isCancelled && (
              <div>
                Reqstd: {format(parse(record.requested, 'yyyy-MM-dd HH:mm:ss', new Date()), 'h:mmaaa dd-MM-yyyy')}
              </div>
            )}
            {record.appointment && !isCancelled && (
              <div>
                Apmnt: {format(parse(record.appointment, 'yyyy-MM-dd HH:mm:ss', new Date()), 'h:mmaaa dd-MM-yyyy')}
              </div>
            )}
          </>
        );
      },
      width: 210,
    },
    {
      key: 'customerName',
      title: 'Customer name',
      dataIndex: ['customer_name'],
      render: (_, record) => <span data-dd-privacy="mask">{record.customer_name}</span>,
      width: 120,
    },
    {
      key: 'address',
      title: 'Address',
      dataIndex: ['customer_address'],
      render: (_, record) => <span data-dd-privacy="mask">{record.customer_address}</span>,
      width: 150,
    },
    {
      key: 'jobDetails',
      title: 'Job Details',
      dataIndex: ['description'],
      render: (_, record) => (
        <div title={record.description} className={styles.jobList__descriptionCell}>
          {record.description}
        </div>
      ),
      width: 260,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_, record) => (
        <div>
          {record.cancelled === Condition.Yes ? 'Cancelled' : record.completed === Condition.Yes ? 'Completed' : 'Open'}
        </div>
      ),
      width: 80,
    },
  ] as const satisfies ColumnsType<JobSearchResult>;

  const actionsRenderer = (actions?: Action[]) =>
    function Actions(_: any, record: JobSearchResult) {
      return (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
          {actions?.map((item, i) =>
            typeof item === 'function' ? (
              <Text.$ key={i}>{item(record)}</Text.$>
            ) : (
              <Link.$
                key={item.title}
                onClick={(e) => {
                  e.preventDefault();
                  item.action(record);
                }}
                href="#"
                className={styles.jobList__action}
              >
                {item.title}
              </Link.$>
            ),
          )}
        </Space.$>
      );
    };

  const defaultActions: Action[] = [
    JobActions.Edit,
    JobActions.Reschedule,
    JobActions.Cancel,
    JobActions.ManageTickets,
    JobActions.ViewDetails,
  ];
  const defaultPageSize = 5;

  export const $: FC<Props> = ({
    jobs,
    actions = defaultActions,
    paginationMetadata: meta,
    hideColumns = [],
    isLoading,
    columnsDefinition,
  }) => {
    const columns = useMemo(() => {
      return (columnsDefinition || (defaultColumns as ColumnsType<JobSearchResult>))
        .concat({
          key: 'actions',
          title: 'Actions',
          render: actionsRenderer(actions),
          width: 120,
        })
        .filter(({ key }) => !(key && (hideColumns as string[]).includes(key as string)));
    }, [defaultColumns, columnsDefinition, actions]);

    if (!jobs) {
      return <Spin data-testid="spinner" className={styles.jobList__spin} />;
    }

    if (jobs && !jobs.length) {
      return (
        <Empty
          styles={{ image: { height: 60 } }}
          style={{ marginBottom: 20 }}
          description={<Text.$ type="secondary">Sorry, there are no matching results.</Text.$>}
        />
      );
    }

    const paginationProps: TablePaginationConfig | undefined =
      meta && meta.total > (meta.pageSize || defaultPageSize)
        ? {
            rootClassName: styles.jobList__pagination,
            total: meta.total,
            current: meta.current_page,
            showTotal: () => <>Total {meta.total} items</>,
            showSizeChanger: true,
            defaultPageSize: meta.pageSize || defaultPageSize,
            pageSizeOptions: [defaultPageSize, 10, 20, 50, 100],
            onChange: typeof meta.onChange === 'function' ? meta.onChange : undefined,
          }
        : undefined;

    return <Table pagination={paginationProps} rowKey={'ID'} loading={isLoading} columns={columns} dataSource={jobs} />;
  };
}
